<script setup lang="ts">
/**
 * Every heading below the page title. Space Grotesk rather than Anton (BG §19.1 is a display face and
 * does not survive being shrunk into a dense list), uppercased and tracked so it still reads as
 * part of the same system.
 */
import { ref } from 'vue'

withDefaults(
    defineProps<{
        /** Heading level. Kept separate from the visual size so the document outline stays correct. */
        as?: string
        /** `system` tints it cyan for observational sections — runs, logs, status. */
        tone?: 'default' | 'system'
    }>(),
    { as: 'h2', tone: 'default' },
)

const root = ref<HTMLElement | null>(null)

/*
 * Several of these headings are focus targets: a view moves focus to them after the selection
 * changes so a screen reader announces the new context. A `ref` on a component yields the component
 * instance rather than the element, so without this the callers' `heading.focus()` silently becomes
 * a TypeError. Exposing it keeps those call sites working as they read.
 */
function focus(options?: FocusOptions): void {
    root.value?.focus(options)
}

function scrollIntoView(options?: ScrollIntoViewOptions): void {
    root.value?.scrollIntoView(options)
}

defineExpose({ focus, scrollIntoView })
</script>

<template>
    <component :is="as" ref="root" class="zh-section-heading" :data-tone="tone">
        <slot />
        <span v-if="$slots.count" class="zh-section-heading__count"><slot name="count" /></span>
    </component>
</template>

<style scoped>
.zh-section-heading {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin: 0;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    color: var(--dirty-white);
}

.zh-section-heading[data-tone='system'] {
    color: var(--electric-cyan);
}

.zh-section-heading__count {
    font-family: var(--font-mono);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: var(--muted-grey);
}
</style>
